import * as Y from 'yjs'
import { Y_ELEMENTS, Y_FRAMES, Y_META } from '@praesto/shared'

/**
 * Ein Dokument aus einem gesicherten Stand aufbauen.
 *
 * Der Aufrufer muss es wieder zerstören — es hängt an nichts und würde sonst
 * bis zum Neuladen der Seite im Speicher stehen bleiben.
 */
export function docFromSnapshot(state: Uint8Array): Y.Doc {
  const doc = new Y.Doc()
  Y.applyUpdate(doc, state)
  return doc
}

/**
 * Einen gesicherten Stand in das lebende Dokument zurückholen.
 *
 * Bewusst als Abgleich Feld für Feld und nicht als Austausch des ganzen
 * Dokuments: Das lebende Dokument hängt an offenen Verbindungen. Ein neues
 * unterzuschieben hieße, jeden anderen Teilnehmer aus einem Dokument zu werfen,
 * das er gerade offen hat — und ein CRDT, das man ersetzt statt ändert, verliert
 * genau die Eigenschaft, für die es da ist.
 *
 * Alles läuft in einer Transaktion: Bei den anderen kommt die Wiederherstellung
 * als **ein** Schritt an, nicht als Regen aus hunderten Einzeländerungen.
 */
export function restoreSnapshot(live: Y.Doc, state: Uint8Array): void {
  const past = docFromSnapshot(state)

  try {
    live.transact(() => {
      for (const name of [Y_ELEMENTS, Y_FRAMES]) {
        const target = live.getMap<Y.Map<unknown>>(name)
        const source = past.getMap<Y.Map<unknown>>(name)

        // Was es im Stand nicht gab, verschwindet.
        for (const key of [...target.keys()]) {
          if (!source.has(key)) target.delete(key)
        }

        source.forEach((entry, key) => {
          const fields = entry instanceof Y.Map ? entry.toJSON() : {}
          const existing = target.get(key)

          if (existing instanceof Y.Map) {
            // Vorhandene ändern statt ersetzen: Nur die Felder, die sich
            // unterscheiden, gehen über das Netz.
            for (const [field, value] of Object.entries(fields)) {
              if (existing.get(field) !== value) existing.set(field, value)
            }
            for (const field of [...existing.keys()]) {
              if (!(field in fields)) existing.delete(field)
            }
            return
          }

          const fresh = new Y.Map<unknown>()
          for (const [field, value] of Object.entries(fields)) fresh.set(field, value)
          target.set(key, fresh)
        })
      }

      // Untergrund, Palette und Schriften gehören zum Stand dazu: Wer den Stand
      // von gestern zurückholt, meint auch dessen Aussehen.
      const meta = live.getMap<unknown>(Y_META)
      const pastMeta = past.getMap<unknown>(Y_META)
      pastMeta.forEach((value, key) => {
        meta.set(key, value instanceof Y.AbstractType ? (value as { toJSON: () => unknown }).toJSON() : value)
      })
    })
  } finally {
    past.destroy()
  }
}
